import {
  useCompletePatientChallenge,
  useDeletePatientChallengeSetting,
  useGetAllPatientChallengesByID,
} from '_api';
import { AddChallengeToPatientPayload, Challenge, ContentSettingV2 } from '_api/types';
import { ALERT_TYPE, ChallengeRouteMap } from '_constants';
import { ThumbnailView } from 'components/atoms';
import { ActionsFlexBox, PatientProfileTable } from 'components/molecules';
import { AddChallengeToPatientProfileModal } from 'components/organisms/modals/AddChallengeToPatientProfileModal';
import dayjs from 'dayjs';
import { MRT_ColumnDef } from 'material-react-table';
import React, { useState, useMemo } from 'react';
import { FaCircleQuestion } from 'react-icons/fa6';
import { useNavigateTo } from 'utils';

import { EditChallengeToPatientProfileModal } from './modals';
import { ProfileTableProps } from './types';

export const PatientChallengeTable = ({ patientId }: ProfileTableProps) => {
  const navigate = useNavigateTo();
  const { data: challenges, isLoading } = useGetAllPatientChallengesByID(patientId);
  const { mutateAsync: deleteChallenge } = useDeletePatientChallengeSetting();
  const { mutateAsync: completeChallenge } = useCompletePatientChallenge();
  const [open, setOpen] = useState(false);
  const handleCloseModal = () => setOpen(false);

  const [editChallenge, setEditChallenge] = useState<
    AddChallengeToPatientPayload | undefined
  >(undefined);
  const [editChallengeLabel, setEditChallengeLabel] = useState<string>('');
  const handleCloseEditModal = () => setEditChallenge(undefined);

  const activePatientChallengesById = useMemo(() => {
    return challenges ? challenges.filter((x) => x.status === 'ACTIVE') : [];
  }, [challenges]);

  const handleEditChallengeOnClick = (challenge: ContentSettingV2<Challenge>) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { content, therapyType, schedules, instructionList, ...data } = challenge;
    const active = schedules?.[schedules.length - 1];
    setEditChallengeLabel(content.title);
    setEditChallenge({
      contentId: content.id,
      patientId: patientId,
      perWeek: active?.perWeek ?? 1,
      perDay: active?.perDay ?? 1,
      schedule: active?.schedule ?? [],
      reps: active?.reps ?? 1,
      sets: active?.sets ?? 1,
      schedules,
      instructionList,
      ...data,
    });
  };

  const handleDeleteChallengeOnClick = (id: number) => {
    deleteChallenge({ id });
  };

  const handleCompleteOnClick = (id: number) => {
    const payload = {
      patientContentId: id,
      completedDate: dayjs().format('YYYY-MM-DD'),
    };
    completeChallenge(payload);
  };

  const columns = useMemo<MRT_ColumnDef<ContentSettingV2<Challenge>>[]>(
    () => [
      {
        header: 'Alert',
        Cell: ({ row }) =>
          row.original.alertType && row.original.alertType !== null ? (
            row.original.alertType === ALERT_TYPE.MISSING ? (
              <FaCircleQuestion
                style={{ color: 'gray', height: '15%', margin: 'auto', width: '35%' }}
              />
            ) : (
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill={row.original.alertType === ALERT_TYPE.RED ? '#DB1823' : '#E6B55E'}
                  d="M12 2L2 22h20L12 2z"
                />
                <path
                  fill={row.original.alertType === ALERT_TYPE.RED ? '#FFFFFF' : '#050000'}
                  d="M11 14v-5h2v5h-2zm0 3v-2h2v2h-2z"
                />
              </svg>
            )
          ) : null,
        size: 10,
      },
      {
        header: 'Challenge',
        Cell: ({ row }) => (
          <ThumbnailView
            fullImageUrl={row.original.content.fullImageUrl}
            rowView={true}
          />
        ),
        size: 10,
      },
      {
        header: 'Title',
        accessorFn: (row) => row.content.title,
      },
      {
        header: 'Assigned Date',
        accessorFn: (row) => dayjs(row?.schedules[0].startDate).format('MM/DD/YYYY'),
        size: 5,
      },
      {
        header: 'Actions',
        accessorFn: (row) => (
          <ActionsFlexBox
            msg={'challenge'}
            editOnClick={() => handleEditChallengeOnClick(row)}
            deleteOnClick={() => handleDeleteChallengeOnClick(row.id)}
            completeOnClick={() => handleCompleteOnClick(row.id)}
            reportOnClick={() => navigate(ChallengeRouteMap.REPORT, row.id.toString())}
            actionButtons={{
              deleteButton: false,
              editButton: true,
              reportButton: true,
              completeButton: true,
            }}
          />
        ),
        size: 10,
      },
    ],
    [],
  );

  return (
    <>
      {challenges && !isLoading ? (
        <PatientProfileTable
          columns={columns}
          data={activePatientChallengesById}
          onAddClick={() => setOpen(true)}
          title={'Functional Challenges'}
        />
      ) : null}
      <AddChallengeToPatientProfileModal
        open={open}
        onClose={handleCloseModal}
        patientId={patientId}
      />
      {editChallenge ? (
        <EditChallengeToPatientProfileModal
          open={editChallenge !== undefined}
          onClose={handleCloseEditModal}
          initValue={editChallenge}
          label={editChallengeLabel}
        />
      ) : null}
    </>
  );
};
